import { API_CONFIG, Package, PackagesResponse, PackagesApiResponse, PackageApiResponse, PackageType, Destination } from '../config/api'
import { expandDestinationIds } from '../utils/packageHelpers'
import { getPriceRange, matchesPriceRange, PriceRange } from '../utils/priceFilter'

export interface PackageFilters {
  category?: string
  search?: string
  destination?: string | number
  offer_type?: string | number
  date_from?: string
  date_to?: string
  price_min?: number
  price_max?: number
  price?: string
  per_page?: number
  query?: string
}

export class PackageService {
  private static instance: PackageService

  private constructor() {}

  public static getInstance(): PackageService {
    if (!PackageService.instance) {
      PackageService.instance = new PackageService()
    }
    return PackageService.instance
  }

  public async getPackages(
    page: number = 1,
    filters: PackageFilters = {}
  ): Promise<PackagesResponse> {
    // The API ignores the price param, so price ranges are filtered client-side
    const priceRange = getPriceRange(filters)
    if (priceRange) {
      return this.getPackagesInPriceRange(page, filters, priceRange)
    }

    try {
      const queryParams = new URLSearchParams({
        franchise_id: API_CONFIG.FRANCHISE_ID,
      })

      // Pagination applies to searches too - the API pages query results like any other listing
      queryParams.append('page', page.toString())
      // Add per_page parameter
      if (filters.per_page) {
        queryParams.append('per_page', filters.per_page.toString())
      } else {
        queryParams.append('per_page', '12') // Default to 12 as shown in API
      }

      // Category filter
      if (filters.category && filters.category !== 'All') {
        queryParams.append('category', filters.category)
      }

      // Search/query filter - use 'query' parameter as per API spec
      if (filters.search) {
        queryParams.append('query', filters.search)
      }

      if (filters.query) {
        queryParams.append('query', filters.query)
      }

      // Destination filter — country destinations can expand to child destination IDs
      if (filters.destination) {
        const destinationIds = expandDestinationIds(filters.destination)
        if (destinationIds.length > 1) {
          return this.getPackagesForDestinationGroup(page, filters, destinationIds)
        }
        queryParams.append('destination', destinationIds[0]?.toString() || filters.destination.toString())
      }

      // Offer type filter
      if (filters.offer_type) {
        queryParams.append('offer_type', filters.offer_type.toString())
      }

      // Date range filters
      if (filters.date_from) {
        queryParams.append('datefrom', filters.date_from)
      }

      if (filters.date_to) {
        queryParams.append('dateto', filters.date_to)
      }

      // Price range filter
      if (filters.price) {
        queryParams.append('price', filters.price)
      } else if (filters.price_min && filters.price_max) {
        queryParams.append('price', `${filters.price_min}-${filters.price_max}`)
      } else if (filters.price_min) {
        queryParams.append('price', `${filters.price_min}-`)
      } else if (filters.price_max) {
        queryParams.append('price', `-${filters.price_max}`)
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/franchise_packages?${queryParams.toString()}`,
        {
          headers: {
            'x-api-key': API_CONFIG.API_KEY,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const apiData: PackagesApiResponse = await response.json()
      return this.transformApiResponse(apiData, page, filters)
    } catch (error) {
      console.error('Error fetching packages:', error)
      // Return fallback data if API fails
      return this.getFallbackData(page, filters)
    }
  }

  private async getPackagesForDestinationGroup(
    page: number,
    filters: PackageFilters,
    destinationIds: number[]
  ): Promise<PackagesResponse> {
    const perPage = filters.per_page || 12
    const results = await Promise.all(
      destinationIds.map(async (destinationId) => {
        try {
          return await this.fetchAllPackagesForDestination(destinationId)
        } catch (error) {
          console.error(`Error fetching packages for destination ${destinationId}:`, error)
          return [] as Package[]
        }
      })
    )

    const unique = new Map<number, Package>()
    results.flat().forEach((pkg) => unique.set(pkg.id, pkg))
    let merged = Array.from(unique.values())

    if (filters.category && filters.category !== 'All') {
      merged = merged.filter(
        (pkg) => pkg.category?.toLowerCase() === filters.category!.toLowerCase()
      )
    }

    const searchTerm = (filters.query || filters.search || '').trim().toLowerCase()
    if (searchTerm) {
      merged = merged.filter(
        (pkg) =>
          pkg.name.toLowerCase().includes(searchTerm) ||
          pkg.location?.toLowerCase().includes(searchTerm) ||
          pkg.description?.toLowerCase().includes(searchTerm) ||
          pkg.intro?.toLowerCase().includes(searchTerm)
      )
    }

    const totalCount = merged.length
    const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
    const currentPage = Math.min(Math.max(page, 1), totalPages)
    const start = (currentPage - 1) * perPage

    return {
      packages: merged.slice(start, start + perPage),
      pagination: {
        current_page: currentPage,
        total_pages: totalPages,
        total_count: totalCount,
        per_page: perPage,
        has_next: currentPage < totalPages,
        has_prev: currentPage > 1,
      },
    }
  }

  private async fetchAllPackagesForDestination(destinationId: number): Promise<Package[]> {
    const packages: Package[] = []
    let page = 1
    let totalPages = 1

    do {
      const queryParams = new URLSearchParams({
        franchise_id: API_CONFIG.FRANCHISE_ID,
        destination: destinationId.toString(),
        page: page.toString(),
        per_page: '50',
      })

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/franchise_packages?${queryParams.toString()}`,
        {
          headers: {
            'x-api-key': API_CONFIG.API_KEY,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const apiData: PackagesApiResponse & { meta?: { total_pages?: number; total_entries?: number } } =
        await response.json()
      const batch = (apiData.franchise_packages || []).map((pkg) => this.transformPackage(pkg))
      packages.push(...batch)
      totalPages = apiData.meta?.total_pages || 1
      page += 1
    } while (page <= totalPages)

    return packages
  }

  // Fetch every package matching the other filters, keep those inside the
  // price range and paginate the result locally
  private async getPackagesInPriceRange(
    page: number,
    filters: PackageFilters,
    priceRange: PriceRange
  ): Promise<PackagesResponse> {
    const perPage = filters.per_page || 12
    const otherFilters: PackageFilters = {
      ...filters,
      price: undefined,
      price_min: undefined,
      price_max: undefined,
      per_page: 500,
    }

    const seen = new Set<number>()
    const matches: PackagesResponse['packages'] = []
    let pageNum = 1
    let totalPages = 1

    do {
      const response = await this.getPackages(pageNum, otherFilters)
      response.packages.forEach((pkg) => {
        if (!seen.has(pkg.id) && matchesPriceRange(pkg, priceRange)) {
          seen.add(pkg.id)
          matches.push(pkg)
        }
      })
      totalPages = response.pagination.total_pages || 1
      pageNum += 1
    } while (pageNum <= Math.min(totalPages, 10))

    const totalCount = matches.length
    const pageCount = Math.max(1, Math.ceil(totalCount / perPage))
    const currentPage = Math.min(Math.max(page, 1), pageCount)
    const start = (currentPage - 1) * perPage

    return {
      packages: matches.slice(start, start + perPage),
      pagination: {
        current_page: currentPage,
        total_pages: pageCount,
        total_count: totalCount,
        per_page: perPage,
        has_next: currentPage < pageCount,
        has_prev: currentPage > 1,
      },
    }
  }

  public async getPackageBySlug(slug: string): Promise<Package> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/franchise_packages/${slug}`,
        {
          headers: {
            'x-api-key': API_CONFIG.API_KEY,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const apiData: PackageApiResponse = await response.json()
      return this.transformPackage(apiData.package)
    } catch (error) {
      console.error('Error fetching package:', error)
      // Return fallback data if API fails
      return this.getFallbackPackage(slug)
    }
  }

  public async getPackageTypes(): Promise<PackageType[]> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/package_types?franchise_id=${API_CONFIG.FRANCHISE_ID}`,
        {
          headers: {
            'x-api-key': API_CONFIG.API_KEY,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.package_types || []
    } catch (error) {
      console.error('Error fetching package types:', error)
      // Return fallback data
      return [
        { id: 90, name: 'Beach Holiday', description: 'Beach and coastal destinations', slug: 'beach-holiday' },
        { id: 91, name: 'City Break', description: 'Urban destinations and city tours', slug: 'city-break' },
        { id: 92, name: 'Safari', description: 'Wildlife and safari experiences', slug: 'safari' },
        { id: 93, name: 'Adventure', description: 'Adventure and outdoor activities', slug: 'adventure' },
        { id: 94, name: 'Luxury', description: 'Premium and luxury travel', slug: 'luxury' },
      ]
    }
  }

  public async getDestinations(): Promise<Destination[]> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/destinations?franchise_id=${API_CONFIG.FRANCHISE_ID}`,
        {
          headers: {
            'x-api-key': API_CONFIG.API_KEY,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.destinations || []
    } catch (error) {
      console.error('Error fetching destinations:', error)
      // Return fallback data
      return [
        { id: 1, name: 'Mauritius', uuid: 'mauritius-uuid', country_code: 'MU', intro: 'Paradise island', slug: 'mauritius', google_places_id: null, latitude: -20.348404, longitude: 57.552152 },
        { id: 2, name: 'Maldives', uuid: 'maldives-uuid', country_code: 'MV', intro: 'Tropical paradise', slug: 'maldives', google_places_id: null, latitude: 3.202778, longitude: 73.22068 },
        { id: 3, name: 'Dubai', uuid: 'dubai-uuid', country_code: 'AE', intro: 'Modern luxury', slug: 'dubai', google_places_id: null, latitude: 25.204849, longitude: 55.270783 },
        { id: 4, name: 'South Africa', uuid: 'south-africa-uuid', country_code: 'ZA', intro: 'Adventure awaits', slug: 'south-africa', google_places_id: null, latitude: -30.559482, longitude: 22.937506 },
      ]
    }
  }

  private transformApiResponse(
    apiData: PackagesApiResponse, 
    page: number, 
    filters: PackageFilters
  ): PackagesResponse {
    // Transform API packages to UI format
    const transformedPackages = apiData.franchise_packages.map(pkg => this.transformPackage(pkg))
    
    // Apply client-side filtering if needed (for fallback or if API doesn't support filtering)
    let filteredPackages = transformedPackages

    if (filters.category && filters.category !== 'All') {
      filteredPackages = filteredPackages.filter(
        pkg => pkg.category?.toLowerCase() === filters.category!.toLowerCase()
      )
    }

    // Only apply client-side search filtering if using the 'search' parameter (fallback)
    // If using 'query' parameter, trust that API has already filtered the results
    if (filters.search && !filters.query) {
      const searchTerm = filters.search.toLowerCase()
      filteredPackages = filteredPackages.filter(
        pkg => 
          pkg.name.toLowerCase().includes(searchTerm) ||
          pkg.location?.toLowerCase().includes(searchTerm) ||
          pkg.description.toLowerCase().includes(searchTerm) ||
          pkg.intro.toLowerCase().includes(searchTerm)
      )
    }
    // For 'query' parameter, let API handle the filtering - don't apply client-side filtering

    // Handle pagination - if API provides meta, use it, otherwise simulate
    const isSearching = filters.search || filters.query
    let pagination
    
    if (apiData.meta) {
      pagination = {
        current_page: apiData.meta.current_page,
        total_pages: apiData.meta.total_pages,
        total_count: apiData.meta.total_count ?? (apiData.meta as { total_entries?: number }).total_entries ?? 0,
        per_page: apiData.meta.per_page ?? (filters.per_page || 12),
        has_next: apiData.meta.current_page < apiData.meta.total_pages,
        has_prev: apiData.meta.current_page > 1,
      }
    } else if (isSearching) {
      // For search, return all results without pagination
      pagination = {
        current_page: 1,
        total_pages: 1,
        total_count: filteredPackages.length,
        per_page: filteredPackages.length,
        has_next: false,
        has_prev: false,
      }
      // Don't slice filteredPackages for search - return all results
    } else {
      // Simulate pagination for regular browsing
      const perPage = 6
      const totalCount = filteredPackages.length
      const totalPages = Math.ceil(totalCount / perPage)
      const startIndex = (page - 1) * perPage
      const endIndex = startIndex + perPage
      filteredPackages = filteredPackages.slice(startIndex, endIndex)
      
      pagination = {
        current_page: page,
        total_pages: totalPages,
        total_count: totalCount,
        per_page: perPage,
        has_next: page < totalPages,
        has_prev: page > 1,
      }
    }

    return {
      packages: filteredPackages,
      pagination
    }
  }

  private transformPackage(apiPackage: Package): Package {
    // Format price from cents to currency string
    const formatPrice = (cents: number, currency: string = 'ZAR') => {
      const amount = cents / 100
      if (currency === 'ZAR') {
        return `R${amount.toLocaleString()}`
      }
      return `${currency} ${amount.toLocaleString()}`
    }

    // Extract duration from nights
    const formatDuration = (nights: number) => {
      return `${nights} night${nights !== 1 ? 's' : ''}`
    }

    // Determine category heuristically only when clearly implied
    const determineCategory = (name: string, description: string, intro: string): string | undefined => {
      const content = `${name || ''} ${description || ''} ${intro || ''}`.toLowerCase()

      if (content.includes('luxury') || content.includes('5 star') || content.includes('royal')) {
        return 'Luxury'
      }
      if (content.includes('golf')) {
        return 'Golf'
      }
      if (content.includes('adults only')) {
        return 'Adults Only'
      }
      if (content.includes('eco') || content.includes('sustainable')) {
        return 'Eco-Friendly'
      }
      if (content.includes('city') || content.includes('dubai') || content.includes('urban')) {
        return 'City Break'
      }
      // Do not force a default; leave undefined when not confidently categorized
      return undefined
    }

    // Extract includes from inclusions HTML
    const extractIncludes = (inclusions: string | null | undefined) => {
      // Basic extraction of list items from HTML
      const includes: string[] = []
      
      if (apiPackage.flight_included) includes.push('Flight Included')
      if (apiPackage.hotel_included) includes.push('Hotel Included')
      if (apiPackage.transfer_included) includes.push('Transfers Included')
      if (apiPackage.guided_tour) includes.push('Guided Tour')
      
      // Extract from HTML if inclusions is a string
      if (inclusions && typeof inclusions === 'string') {
        const listItems = inclusions.match(/<li>(.*?)<\/li>/g)
        if (listItems) {
          listItems.slice(0, 3).forEach(item => { // Limit to first 3 items
            const text = item.replace(/<[^>]*>/g, '').trim()
            // Check for valid text and limit length
            if (text && text.length < 50) { 
              includes.push(text)
            }
          })
        }
      }
      
      return includes.slice(0, 4) // Limit to 4 items max
    }

    // Format travel dates
    const formatDates = (travelFrom: string, travelTo: string) => {
      try {
        const from = new Date(travelFrom).toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        })
        const to = new Date(travelTo).toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        })
        return `${from} - ${to}`
      } catch {
        return `${travelFrom} - ${travelTo}`
      }
    }

    // Get location from destination object or fallback to mapping
    const getLocation = () => {
      if (apiPackage.destination?.name) {
        return apiPackage.destination.name
      }
      return this.getLocationFromDestinationId(apiPackage.destination_id)
    }

    return {
      ...apiPackage,
      // Computed UI fields
      formatted_price: formatPrice(apiPackage.price_cents, apiPackage.price_currency),
      duration: formatDuration(apiPackage.nights),
      category: determineCategory(apiPackage.name, apiPackage.description, apiPackage.intro),
      includes: extractIncludes(apiPackage.inclusions),
      rating: 5, // Default rating since not provided by API
      location: getLocation(),
      dates: formatDates(apiPackage.travel_from, apiPackage.travel_to),
    }
  }

  private getFallbackPackage(slug: string): Package {
    // Return a single fallback package for individual package view
    return {
      id: 1,
      uuid: 'fallback-package',
      name: 'Sample Holiday Package',
      location: 'Mauritius',
      formatted_price: 'R45,000',
      rating: 5,
      dates: '01/12/2025 - 10/12/2025',
      duration: '7 nights',
      description: 'A beautiful holiday package with all the amenities you need for a perfect getaway.',
      includes: ['Flight Included', 'Hotel Included', 'Meals Included'],
      category: 'Beach',
      // API fields
      intro: 'A beautiful holiday package with stunning views and excellent service.',
      inclusions: '<ul><li>Flight Included</li><li>Hotel Included</li><li>Meals Included</li></ul>',
      exclusions: '<ul><li>Personal expenses</li><li>Travel insurance</li></ul>',
      terms: 'Standard terms and conditions apply. Please contact us for full details.',
      published: true,
      valid_from: '2025-01-01',
      valid_to: '2025-12-31',
      travel_from: '2025-12-01',
      travel_to: '2025-12-10',
      price_cents: 4500000,
      price_currency: 'ZAR',
      nights: 7,
      flight_included: true,
      hotel_included: true,
      transfer_included: true,
      guided_tour: false,
      self_drive: false,
      total_pax: '2 adults',
      destination_id: 11,
      destination: {
        id: 11,
        name: 'Mauritius',
        uuid: 'mauritius-fallback',
        country_code: 'MU',
        intro: 'Beautiful tropical island destination',
        slug: 'mauritius',
        google_places_id: null,
        latitude: null,
        longitude: null,
      },
      slug: slug || 'sample-holiday-package',
      cover_photo: null,
      photos: [],
    }
  }

  private getLocationFromDestinationId(destinationId: number): string {
    // Map destination IDs to location names - you may need to adjust these
    const destinationMap: { [key: number]: string } = {
      11: 'Mauritius',
      1: 'Dubai',
      2: 'Cape Town',
      3: 'Durban',
      4: 'Johannesburg',
      5: 'Thailand',
      6: 'Seychelles',
      7: 'Maldives',
      8: 'Zanzibar',
      9: 'Kenya',
      10: 'Botswana',
      // Add more as needed
    }
    
    return destinationMap[destinationId] || 'International'
  }

  private getFallbackData(page: number, filters: PackageFilters): PackagesResponse {
    // Fallback data based on the original static packages
    const allPackages: Package[] = [
      {
        id: 1,
        uuid: 'fallback-1',
        name: 'Royal Palm Beachcomber Luxury - 5 star luxury',
        location: 'Mauritius',
        formatted_price: 'R77,430',
        rating: 5,
        dates: '01/12/2025 - 10/12/2025',
        duration: '7 nights',
        description: 'The most exclusive address in the Indian Ocean, this prestigious property is listed among the Leading Hotels of the World...',
        includes: ['Flight Included', 'Hotel Included', 'All Meals'],
        category: 'Luxury',
        // API fields
        intro: 'The most exclusive address in the Indian Ocean',
        inclusions: '<ul><li>Flight Included</li><li>Hotel Included</li><li>All Meals</li></ul>',
        exclusions: '<ul><li>Personal expenses</li></ul>',
        published: true,
        valid_from: '2025-01-01',
        valid_to: '2025-12-31',
        travel_from: '2025-12-01',
        travel_to: '2025-12-10',
        price_cents: 7743000,
        price_currency: 'ZAR',
        nights: 7,
        flight_included: true,
        hotel_included: true,
        transfer_included: true,
        guided_tour: false,
        self_drive: false,
        total_pax: '2 adults',
        destination_id: 11,
        slug: 'royal-palm-beachcomber-luxury-5-star-luxury',
        cover_photo: null,
      },
      {
        id: 2,
        uuid: 'fallback-2',
        name: 'Dinarobin Beachcomber Golf Resort & Spa - 5 star',
        location: 'Mauritius',
        formatted_price: 'R44,810',
        rating: 5,
        dates: '01/12/2025 - 10/12/2025',
        duration: '7 nights',
        description: 'An elegant five-star resort boasting a championship golf course and world-class spa...',
        includes: ['Flight Included', 'Hotel Included', 'Golf Access'],
        category: 'Golf',
        // API fields
        intro: 'An elegant five-star resort boasting a championship golf course',
        inclusions: '<ul><li>Flight Included</li><li>Hotel Included</li><li>Golf Access</li></ul>',
        exclusions: '<ul><li>Personal expenses</li></ul>',
        published: true,
        valid_from: '2025-01-01',
        valid_to: '2025-12-31',
        travel_from: '2025-12-01',
        travel_to: '2025-12-10',
        price_cents: 4481000,
        price_currency: 'ZAR',
        nights: 7,
        flight_included: true,
        hotel_included: true,
        transfer_included: true,
        guided_tour: false,
        self_drive: false,
        total_pax: '2 adults',
        destination_id: 11,
        slug: 'dinarobin-beachcomber-golf-resort-spa-5-star',
        cover_photo: null,
      },
      // Add more fallback packages as needed...
    ]

    // Apply filters
    let filteredPackages = allPackages

    if (filters.category && filters.category !== 'All') {
      filteredPackages = filteredPackages.filter(
        pkg => pkg.category?.toLowerCase() === filters.category!.toLowerCase()
      )
    }

    // Apply search filter (fallback data)
    if (filters.search || filters.query) {
      const searchTerm = (filters.search || filters.query || '').toLowerCase()
      filteredPackages = filteredPackages.filter(
        pkg => 
          pkg.name.toLowerCase().includes(searchTerm) ||
          pkg.location?.toLowerCase().includes(searchTerm) ||
          pkg.description.toLowerCase().includes(searchTerm)
      )
    }

    // Simulate pagination
    const perPage = 6
    const totalCount = filteredPackages.length
    const totalPages = Math.ceil(totalCount / perPage)
    const startIndex = (page - 1) * perPage
    const endIndex = startIndex + perPage
    const packages = filteredPackages.slice(startIndex, endIndex)

    return {
      packages,
      pagination: {
        current_page: page,
        total_pages: totalPages,
        total_count: totalCount,
        per_page: perPage,
        has_next: page < totalPages,
        has_prev: page > 1,
      }
    }
  }
} 